<template>
  <teleport to="body">
    <div class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-content overflow-auto" @click.stop style="max-height: 90vh">
        <!-- Price in top right -->
        <div class="title-price-row d-flex justify-content-between align-items-center mb-4">
          <h5 class="mb-0"> {{ house?.title }}</h5>
          <strong class="price-text">${{ house?.price }}</strong>
        </div>

        <div class="house-details-container">
          <!-- Main Thumbnail -->
          <div class="thumbnail-wrapper">
            <div class="mb-2"><strong></strong> {{ house?.address }}</div>


            <div class="thumbnail-container position-relative">
              <div class="status-badge">{{ statusLabel }}</div>

              <img v-if="house?.thumbnail" :src="`${imgUrl}/uploads/thumbnails/${house.thumbnail}`" alt="Thumbnail"
                class="thumbnail-img" @click="openLightbox(0)" />

              <div v-else
                class="w-100 h-100 object-fit-cover bg-light d-flex align-items-center justify-content-center text-muted"
                style="min-height: 200px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);">
                📷 No picture
              </div>
            </div>
            <!-- Image Gallery -->
            <div v-if="house?.images?.length" class="mb-4 w-40">
              <div class="gallery-wrapper mt-2 d-flex">
                <img v-for="(img, index) in house.images" :key="img.id" :src="getImageUrl(img.imageName)"
                  class="gallery-image" :alt="img.imageName" @click="openLightbox(index + 1)" />
              </div>
            </div>
            <div class="house-icons d-flex justify-content-around text-center mt-4 mb-3">
              <div>
                <font-awesome-icon icon="bed" />
                <div>{{ house?.numBedrooms }} Dorm</div>
              </div>
              <div>
                <font-awesome-icon icon="bath" />
                <div>{{ house?.numBathrooms }} Baños</div>
              </div>
              <div>
                <font-awesome-icon icon="ruler-combined" />
                <div>{{ house?.surface }} m²</div>
              </div>
              <div v-if="house?.hasElevator">
                <font-awesome-icon icon="elevator" />
                <div>Ascensor</div>
              </div>
              <div>
                <font-awesome-icon icon="layer-group" />
                <div>{{ house?.floor || 'N/A' }} </div>
              </div>

            </div>
          </div>


        </div>

        <!-- Address, Financing, Description -->
        <div class="extra-info mb-4">
          <div class="mb-2">{{ house?.financingNote }}</div>
          <div class="mb-2"><strong>Descripción<br/></strong> {{ house?.description }}</div>
        </div>



        <!-- Lightbox -->
        <VueEasyLightbox :visible="lightboxVisible" :imgs="imageUrls" :index="currentIndex"
          @hide="lightboxVisible = false" />

        <!-- Amenities -->
        <div class="mb-3">
          <strong>Amenidades<br/></strong>
          <ul v-if="house?.amenities?.length">
            <li v-for="a in house.amenities" :key="a">
              {{ getAmenityName(a) }}
            </li>
          </ul>
          <span v-else class="text-muted">Sin amenidades</span>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import VueEasyLightbox from 'vue-easy-lightbox'

const props = defineProps({
  house: {
    type: Object,
    required: true
  }
})

const imgUrl = process.env.VUE_APP_API

const statusLabel = computed(() => {
  const map = {
    SELLE: 'Vender',
    RENT: 'Alquilar',
    NEW_PROPERTY: 'Nueva Propiedad',
    VACATION_RENTAL: 'Alquiler Vacacional'
  }
  return map[props.house?.status] || props.house?.status || 'Sin estado'
})

const getImageUrl = (fileName) => `${imgUrl}/uploads/houses/${fileName}`

const getAmenityName = (a) => {
  if (typeof a === 'object' && a.name) return a.name
  if (typeof a === 'string') {
    const parts = a.split('/')
    return parts[parts.length - 1] || a
  }
  return 'Amenidad desconocida'
}

const lightboxVisible = ref(false)
const currentIndex = ref(0)

const imageUrls = computed(() => {
  const thumbnailUrl = props.house?.thumbnail
    ? `${imgUrl}/uploads/thumbnails/${props.house.thumbnail}`
    : null

  const galleryUrls = props.house?.images?.map(img =>
    getImageUrl(img.imageName)
  ) || []

  return thumbnailUrl ? [thumbnailUrl, ...galleryUrls] : galleryUrls
})

const openLightbox = (index) => {
  currentIndex.value = index
  lightboxVisible.value = true
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 10px;
  width: 90%;
  max-width: 750px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
  max-height: 90vh;
}

.top-price {
  display: flex;
  justify-content: flex-end;
}

.price-text {
  font-weight: bold;
  font-size: 1.3rem;
  color: #02053A;
}

.house-details-container {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.thumbnail-wrapper {
  flex: 1 1 45%;
}



.house-icons div {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.9rem;
}

.house-icons .fa-icon {
  font-size: 1.2rem;
  margin-bottom: 4px;
}

.extra-info {
  font-size: 0.95rem;
}

.thumbnail-img {
  width: 100%;
  max-height: 350px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.thumbnail-container {
  position: relative;
}

.status-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: white;
  color: #02053A;
  border-radius: 999px;
  padding: 4px 12px;
  font-weight: 600;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.gallery-wrapper {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.gallery-image {
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.gallery-image:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

@media (max-width: 480px) {
  .gallery-wrapper {
    grid-template-columns: repeat(3, 1fr);
  }

  .thumbnail-img {
    max-height: 250px;
  }
}
</style>
